import { useQuery } from '@tanstack/react-query';
import { pullRequestService } from '../services/pullRequestService';
import type { ListPullRequestsOptions } from '@codemerlin/shared/types/pull-request';

/**
 * React hook for fetching pull requests with latest review data
 * Uses TanStack Query for caching and automatic refetching
 * @param organizationId - The organization ID (hook is disabled if undefined)
 * @param options - Query options including filters, sort, pagination
 * @returns TanStack Query result with pull request data
 */
export function usePullRequests(
  organizationId: string | undefined,
  options: ListPullRequestsOptions = {}
) {
  return useQuery({
    queryKey: ['pull-requests', organizationId, options],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID is required');
      return pullRequestService.listPullRequests(organizationId, options);
    },
    enabled: !!organizationId,
    staleTime: 10000,
    refetchInterval: 10000, // tightened from 30s for queued/running review visibility
    // intentionally overrides the global refetchOnWindowFocus: false default (query-client.ts)
    // so returning to this tab re-validates in-flight review state immediately
    refetchOnWindowFocus: true,
  });
}
